import {
  buildKittingPickSlotsQuery,
  KITTING_PICK_SLOTS_QUERY_KEY,
} from "./kittingOrderSelectionModel";

/**
 * Cart bin label data matrix scan format — see
 * `src/mrp/kitting-cart-label-scan.ts`.
 *
 * Printed payload: `KIT|{slotKey}` (e.g. `KIT|batch-42:station-5`).
 * Pick page also accepts bare bin letters `A`–`G` when cart marks are active.
 */
export {
  buildKittingCartLabelScanPayload,
  KITTING_CART_LABEL_SCAN_PREFIX,
  resolveKittingCartScanToSlotKey,
  type KittingCartScanResolution,
} from "@integrator-src/mrp/kitting-cart-label-scan";

/** Max distinct cart-bin colors/letters per kitting pick session. */
export const MAX_KITTING_CART_MARKS = 7;

export const KITTING_PICK_MARKS_QUERY_KEY = "marks";

export type KittingCartLetter = "A" | "B" | "C" | "D" | "E" | "F" | "G";

export interface KittingCartMarkPaletteEntry {
  letter: KittingCartLetter;
  /** Selected row: left accent bar + tinted background. */
  rowAccentClass: string;
  /** Circular letter badge on modal / pick header. */
  badgeClass: string;
  /** Chip accent on pick workspace headers. */
  chipClass: string;
  /** Monochrome ZPL banner text (thermal printers). */
  zplBannerLabel: string;
}

/**
 * Fixed palette — A through G in selection order.
 * Anchor is always letter A; each additional checked job gets the next letter.
 */
export const KITTING_CART_MARK_PALETTE: readonly KittingCartMarkPaletteEntry[] = [
  {
    letter: "A",
    rowAccentClass: "border-l-amber-500 bg-amber-50/60",
    badgeClass: "bg-amber-500 text-white",
    chipClass: "border-amber-300 bg-amber-50/80",
    zplBannerLabel: "BIN A",
  },
  {
    letter: "B",
    rowAccentClass: "border-l-blue-600 bg-blue-50/60",
    badgeClass: "bg-blue-600 text-white",
    chipClass: "border-blue-300 bg-blue-50/80",
    zplBannerLabel: "BIN B",
  },
  {
    letter: "C",
    rowAccentClass: "border-l-rose-500 bg-rose-50/60",
    badgeClass: "bg-rose-500 text-white",
    chipClass: "border-rose-300 bg-rose-50/80",
    zplBannerLabel: "BIN C",
  },
  {
    letter: "D",
    rowAccentClass: "border-l-emerald-600 bg-emerald-50/60",
    badgeClass: "bg-emerald-600 text-white",
    chipClass: "border-emerald-300 bg-emerald-50/80",
    zplBannerLabel: "BIN D",
  },
  {
    letter: "E",
    rowAccentClass: "border-l-violet-600 bg-violet-50/60",
    badgeClass: "bg-violet-600 text-white",
    chipClass: "border-violet-300 bg-violet-50/80",
    zplBannerLabel: "BIN E",
  },
  {
    letter: "F",
    rowAccentClass: "border-l-lime-600 bg-lime-50/60",
    badgeClass: "bg-lime-600 text-white",
    chipClass: "border-lime-300 bg-lime-50/80",
    zplBannerLabel: "BIN F",
  },
  {
    letter: "G",
    rowAccentClass: "border-l-teal-600 bg-teal-50/60",
    badgeClass: "bg-teal-600 text-white",
    chipClass: "border-teal-300 bg-teal-50/80",
    zplBannerLabel: "BIN G",
  },
];

export interface KittingCartMark extends KittingCartMarkPaletteEntry {
  slotKey: string;
}

const LETTER_BY_CHAR = new Map(
  KITTING_CART_MARK_PALETTE.map((entry) => [entry.letter, entry] as const),
);

export function isKittingCartLetter(value: string): value is KittingCartLetter {
  return LETTER_BY_CHAR.has(value as KittingCartLetter);
}

/** Anchor first, then other selected slot keys in Set insertion order. */
export function orderedKittingSelectedSlotKeys(
  anchorSlotKey: string,
  selectedSlotIds: ReadonlySet<string>,
): string[] {
  const ordered = [anchorSlotKey];
  for (const slotKey of selectedSlotIds) {
    if (slotKey !== anchorSlotKey) ordered.push(slotKey);
  }
  return ordered;
}

export function assignKittingCartMarks(orderedSlotKeys: readonly string[]): Map<string, KittingCartMark> {
  const marks = new Map<string, KittingCartMark>();
  for (let index = 0; index < orderedSlotKeys.length && index < MAX_KITTING_CART_MARKS; index++) {
    const slotKey = orderedSlotKeys[index]!;
    const palette = KITTING_CART_MARK_PALETTE[index];
    if (palette == null) continue;
    marks.set(slotKey, { slotKey, ...palette });
  }
  return marks;
}

export function canAddKittingCartSlot(selectedCount: number): boolean {
  return selectedCount < MAX_KITTING_CART_MARKS;
}

/** `marks=A:batch-42,B:wo-wo:SBO-1` — letter and slot key split on the first colon. */
export function buildKittingPickMarksQuery(marks: ReadonlyMap<string, KittingCartMark>): string {
  const entries = [...marks.values()].sort((left, right) => left.letter.localeCompare(right.letter));
  return entries.map((mark) => `${mark.letter}:${mark.slotKey}`).join(",");
}

export function parseKittingPickMarks(raw: string | null | undefined): Map<string, KittingCartMark> {
  const marks = new Map<string, KittingCartMark>();
  if (raw == null || raw.trim() === "") return marks;

  for (const part of raw.split(",")) {
    const trimmed = part.trim();
    if (trimmed.length < 3) continue;
    const colonIndex = trimmed.indexOf(":");
    if (colonIndex <= 0) continue;
    const letterRaw = trimmed.slice(0, colonIndex).trim();
    const slotKey = trimmed.slice(colonIndex + 1).trim();
    if (!isKittingCartLetter(letterRaw) || slotKey === "") continue;
    const palette = LETTER_BY_CHAR.get(letterRaw);
    if (palette == null) continue;
    marks.set(slotKey, { slotKey, ...palette });
  }
  return marks;
}

/** Uses explicit `marks` when present; otherwise assigns A… from `slots` order. */
export function resolveKittingCartMarks(
  slotIds: readonly string[],
  marksParam: string | null | undefined,
): Map<string, KittingCartMark> {
  const parsed = parseKittingPickMarks(marksParam);
  if (parsed.size > 0) return parsed;
  return assignKittingCartMarks(slotIds);
}

export function kittingCartMarksFromRunRecord(
  slotKeys: readonly string[],
  cartMarks: Record<string, string>,
): Map<string, KittingCartMark> {
  const marks = new Map<string, KittingCartMark>();
  for (const slotKey of slotKeys) {
    const letterRaw = cartMarks[slotKey]?.trim().toUpperCase() ?? "";
    if (!isKittingCartLetter(letterRaw)) continue;
    const palette = LETTER_BY_CHAR.get(letterRaw);
    if (palette == null) continue;
    marks.set(slotKey, { slotKey, ...palette });
  }
  if (marks.size > 0) return marks;
  return assignKittingCartMarks(slotKeys);
}

export function buildKittingPickPageSearchParams(orderedSlotKeys: readonly string[]): URLSearchParams {
  const marks = assignKittingCartMarks(orderedSlotKeys);
  const params = new URLSearchParams();
  params.set(KITTING_PICK_SLOTS_QUERY_KEY, buildKittingPickSlotsQuery(orderedSlotKeys));
  const marksQuery = buildKittingPickMarksQuery(marks);
  if (marksQuery !== "") {
    params.set(KITTING_PICK_MARKS_QUERY_KEY, marksQuery);
  }
  return params;
}

export function kittingCartMarkForSlot(
  marks: ReadonlyMap<string, KittingCartMark>,
  slotKey: string,
): KittingCartMark | null {
  return marks.get(slotKey) ?? null;
}

/** Letter → slot key for cart bin label scans (`A`–`G`). */
export function kittingCartLetterToSlotKeyMap(
  marks: ReadonlyMap<string, KittingCartMark>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const mark of marks.values()) {
    map.set(mark.letter, mark.slotKey);
  }
  return map;
}

export function kittingCartMarkRowClasses(mark: KittingCartMark | null, selected: boolean): string {
  if (!selected || mark == null) {
    return "border-slate-200 bg-white";
  }
  return mark.rowAccentClass;
}

export function compareKittingPickLinesByCartLetter(
  left: { destinationSlotId: string; destinationLabel: string },
  right: { destinationSlotId: string; destinationLabel: string },
  marksBySlotId: ReadonlyMap<string, KittingCartMark>,
): number {
  const leftMark = marksBySlotId.get(left.destinationSlotId);
  const rightMark = marksBySlotId.get(right.destinationSlotId);
  if (leftMark != null && rightMark != null) {
    return leftMark.letter.localeCompare(rightMark.letter);
  }
  if (leftMark != null) return -1;
  if (rightMark != null) return 1;
  return left.destinationLabel.localeCompare(right.destinationLabel, undefined, { sensitivity: "base" });
}

/** Job line picker order: cart letter A through G, then unmarked destinations by label. */
export function sortKittingPickLinesByCartLetter<T extends { destinationSlotId: string; destinationLabel: string }>(
  lines: readonly T[],
  marksBySlotId: ReadonlyMap<string, KittingCartMark>,
): T[] {
  if (marksBySlotId.size === 0 || lines.length < 2) {
    return [...lines];
  }
  return [...lines].sort((left, right) =>
    compareKittingPickLinesByCartLetter(left, right, marksBySlotId),
  );
}
